/**
 * Combobox ricercabile per liste lunghe: CER/EER, attività R/D, ADR.
 * Campo con ricerca: input testuale "Inserisci un carattere per ricercare…"
 * con suggerimenti che mostrano codice + descrizione.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import type { RentriChoice } from "@/lib/rentriChoices";

interface ChoiceComboboxProps {
  id?: string;
  value: string;
  onChange: (value: string, choice: RentriChoice | null) => void;
  options: RentriChoice[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Numero massimo di suggerimenti mostrati (default 12). */
  maxResults?: number;
  /** Mostra la descrizione dell'opzione corrente sotto al campo. */
  showDescription?: boolean;
  className?: string;
  /** Formattazione visualizzazione (es. "17.04.05"). */
  formatValue?: (value: string) => string;
}

export function ChoiceCombobox({
  id,
  value,
  onChange,
  options,
  placeholder = "Inserisci un carattere per ricercare…",
  disabled,
  loading,
  maxResults = 12,
  showDescription = true,
  className,
  formatValue,
}: ChoiceComboboxProps) {
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);

  const selected = options.find((o) => o.value === value) ?? null;
  const display = query !== "" ? query : selected ? (formatValue ? formatValue(selected.value) : selected.label) : "";

  const q = query.trim().toLowerCase().replace(/\./g, "");
  const filtered = q
    ? options
        .filter((o) => {
          const code = o.value.toLowerCase().replace(/\./g, "");
          const desc = (o.description ?? "").toLowerCase();
          return code.includes(q) || desc.includes(q) || o.label.toLowerCase().includes(q);
        })
        .slice(0, maxResults)
    : [];

  const handleBlur = () => {
    // Il testo digitato non è un valore: se identifica una sola opzione la
    // selezioniamo, altrimenti lo scartiamo per non mostrare un campo
    // apparentemente compilato ma vuoto nel form.
    if (query.trim() !== "") {
      if (filtered.length === 1) onChange(filtered[0].value, filtered[0]);
      setQuery("");
    }
    // Lascia tempo all'eventuale click sull'item
    setTimeout(() => setOpen(false), 120);
  };

  const handlePick = (opt: RentriChoice) => {
    onChange(opt.value, opt);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        id={id}
        value={display}
        placeholder={placeholder}
        disabled={disabled}
        className="font-mono"
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(v);
          setOpen(true);
          // Se l'utente cancella tutto resetta la scelta
          if (v.trim() === "") onChange("", null);
        }}
      />
      {open && (loading || filtered.length > 0) && (
        <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-popover text-sm shadow-md">
          {loading && (
            <li className="px-3 py-2 text-xs text-muted-foreground">Caricamento…</li>
          )}
          {filtered.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(opt)}
                className="block w-full px-3 py-2 text-left hover:bg-accent focus:bg-accent focus:outline-none"
              >
                <span className="font-mono text-xs">
                  {formatValue ? formatValue(opt.value) : opt.label}
                </span>
                {opt.description && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {opt.description}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {showDescription && selected?.description && query === "" && (
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
          {selected.description}
        </p>
      )}
    </div>
  );
}
